import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Typography,
  Icons,
  useTheme,
  useMediaQuery,
  Alert,
  Badge,
} from '@ublend-npm/aulaui-core';
import CircularProgress from '@mui/material/CircularProgress';
import { useDispatch, useSelector } from 'react-redux';
import { toggleWidgetPreference } from '@app/actions/home/widgetPreferences';
import { WidgetCard } from '../common';
import OutlookInbox from './OutlookInbox';
import OutlookAllEmailsDialog from './OutlookAllEmailsDialog';
import OutlookThreadDialog from './OutlookThreadDialog';
import LinkIcon from '@mui/icons-material/Link';
import useOutlookConnect from './useOutlookConnect';
import useOutlookEmails from './useOutlookEmails';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { outLookWidgetLogoUrl } from '@core/constants/launchDarklyFeatureFlags';

const WIDGET_KEY = 'home-outlook-widget';

type OutlookView =
  | 'loading'
  | 'connect'
  | 'inbox'
  | 'load-error'
  | 'expired'
  | 'connect-error';

type OutlookErrorStateProps = {
  type: Exclude<OutlookView, 'connect' | 'inbox' | 'loading'>;
  onAction: () => void;
};

const OutlookErrorState = ({ type, onAction }: OutlookErrorStateProps) => {
  const { palette } = useTheme();
  const isDarkMode = palette.mode === 'dark';
  const content = {
    'load-error': {
      title: "We couldn't load your emails",
      message: 'Something went wrong while fetching your emails. Try again',
      button: 'Try again',
      background: '#FFF3E0',
      color: '#754500',
      darkBackground: '#FFF4E5',
      darkColor: '#663C00',
      icon: (
        <Alert
          severity="warning"
          variant="standard"
          aria-label="Warning"
          sx={{
            minWidth: 24,
            p: 0,
            bgcolor: 'transparent',
            color: '#F57C00',
            '& .MuiAlert-icon': { m: 0, p: 0 },
            '& .MuiAlert-message, & .MuiAlert-action': { display: 'none' },
          }}
        />
      ),
    },
    expired: {
      title: 'Your connection has expired',
      message: 'Reconnect to continue viewing your outlook emails',
      button: 'Reconnect account',
      background: '#FDEBEC',
      color: '#722525',
      darkBackground: '#1B0909',
      darkColor: '#FFCDD2',
      icon: (
        <Alert
          severity="error"
          variant="standard"
          aria-label="Error"
          sx={{
            minWidth: 22,
            p: 0,
            bgcolor: 'transparent',
            color: '#E53935',
            '& .MuiAlert-icon': { m: 0, p: 0 },
            '& .MuiAlert-message, & .MuiAlert-action': { display: 'none' },
          }}
        />
      ),
    },
    'connect-error': {
      title: 'Unable to connect',
      message: "We couldn't connect to Microsoft. Please try again.",
      button: 'Retry',
      background: '#FFF3E0',
      color: '#754500',
      darkBackground: '#1A1200',
      darkColor: '#FFE2B7',
      icon: (
        <Alert
          severity="warning"
          variant="standard"
          aria-label="Warning"
          sx={{
            minWidth: 24,
            p: 0,
            bgcolor: 'transparent',
            color: '#F57C00',
            '& .MuiAlert-icon': { m: 0, p: 0 },
            '& .MuiAlert-message, & .MuiAlert-action': { display: 'none' },
          }}
        />
      ),
    },
  }[type];

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={2}
      justifyContent="center"
      flex={1}
      px={1}
      py={2}
    >
      <Box
        display="flex"
        alignItems="flex-start"
        gap={1.5}
        p={2}
        sx={{
          bgcolor: isDarkMode ? content.darkBackground : content.background,
          borderRadius: '4px',
        }}
      >
        {content.icon}
        <Box color={isDarkMode ? content.darkColor : content.color}>
          <Typography variant="subtitle2" fontWeight="bold">
            {content.title}
          </Typography>
          <Typography variant="body2" mt={0.5}>
            {content.message}
          </Typography>
        </Box>
      </Box>

      <Button kind="primary" fullWidth onClick={onAction}>
        {content.button}
      </Button>
    </Box>
  );
};

const OutlookWidget = () => {
  const dispatch = useDispatch<any>();
  const theme = useTheme();
  const { palette } = theme;
  const isDarkMode = palette.mode === 'dark';
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  // --- Connection state (OAuth) ---
  const {
    isConnected,
    needsReauth,
    isLoadingStatus,
    errorCode,
    isConnecting,
    connect,
    disconnect,
  } = useOutlookConnect();

  // --- Email data (only active when connected) ---
  const outlookEmails = useOutlookEmails(isConnected);
  const flags = useFlags();
  const outlookWidgetLogoURL = outLookWidgetLogoUrl(flags);

  // --- Dialog state ---
  const [isAllEmailsOpen, setIsAllEmailsOpen] = useState(false);
  const [returnToAllEmails, setReturnToAllEmails] = useState(false);

  useEffect(() => {
    if (!isConnected) {
      setIsAllEmailsOpen(false);
      setReturnToAllEmails(false);
    }
  }, [isConnected]);

  // --- Derived view ---
  const outlookView: OutlookView = useMemo(() => {
    if (isLoadingStatus) return 'loading';
    if (needsReauth) return 'expired';
    if (!isConnected) return errorCode ? 'connect-error' : 'connect';
    if (outlookEmails.emailsError) return 'load-error';
    return 'inbox';
  }, [
    isConnected,
    needsReauth,
    isLoadingStatus,
    errorCode,
    outlookEmails.emailsError,
  ]);

  const location = useSelector(
    (state: any) => state.routing?.locationBeforeTransitions
  );
  const pathname = location?.pathname ?? '';

  const isThreadDialogOpen =
    !isSmallScreen &&
    (outlookEmails.isLoadingDetail || Boolean(outlookEmails.selectedThread));

  // Sync route on desktop when direct navigating or refreshing
  useEffect(() => {
    if (!isSmallScreen) {
      if (pathname === '/home/all-emails') {
        setIsAllEmailsOpen(true);
      } else if (pathname === '/home') {
        setIsAllEmailsOpen(false);
      } else {
        const emailMatch = pathname.match(/\/home\/email\/(.+)/);
        if (emailMatch) {
          outlookEmails.openEmailDetail(emailMatch[1]);
        }
      }
    }
  }, [pathname, isSmallScreen, outlookEmails.openEmailDetail]);

  // --- Handlers ---

  const handleConnect = useCallback(() => {
    connect();
  }, [connect]);

  const handleViewAll = useCallback(() => {
    dispatch({
      type: '@@router/CALL_HISTORY_METHOD',
      payload: {
        method: 'push',
        args: ['/home/all-emails'],
      },
    });
    if (!isSmallScreen) {
      setIsAllEmailsOpen(true);
    }
  }, [dispatch, isSmallScreen]);

  const handleRefresh = useCallback(() => {
    outlookEmails.refresh();
  }, [outlookEmails.refresh]);

  const handleRetryLoad = useCallback(() => {
    outlookEmails.refresh();
  }, [outlookEmails.refresh]);

  const handleReconnect = useCallback(() => {
    disconnect();
  }, [disconnect]);

  const handleMessageClick = useCallback(
    (emailId: string, openedFromAllEmails = false) => {
      dispatch({
        type: '@@router/CALL_HISTORY_METHOD',
        payload: {
          method: 'push',
          args: [`/home/email/${emailId}`],
        },
      });
      if (!isSmallScreen) {
        if (!openedFromAllEmails) {
          setIsAllEmailsOpen(false);
        }
        setReturnToAllEmails(openedFromAllEmails);
        outlookEmails.openEmailDetail(emailId);
      }
      // Optimistically mark as read in local state (no API call for now)
      outlookEmails.markEmailRead(emailId);
    },
    [
      dispatch,
      isSmallScreen,
      outlookEmails.markEmailRead,
      outlookEmails.openEmailDetail,
    ]
  );

  const handleAllEmailsClose = useCallback(() => {
    setIsAllEmailsOpen(false);
    dispatch({
      type: '@@router/CALL_HISTORY_METHOD',
      payload: {
        method: 'push',
        args: ['/home'],
      },
    });
  }, [dispatch]);

  const handleThreadClose = useCallback(() => {
    outlookEmails.closeThread();
    const destination = returnToAllEmails ? '/home/all-emails' : '/home';
    dispatch({
      type: '@@router/CALL_HISTORY_METHOD',
      payload: {
        method: 'push',
        args: [destination],
      },
    });
    if (!returnToAllEmails && !isSmallScreen) {
      setIsAllEmailsOpen(false);
    }
    setReturnToAllEmails(false);
  }, [dispatch, returnToAllEmails, isSmallScreen, outlookEmails.closeThread]);

  const handleCompose = useCallback(() => {
    window.open(
      'https://outlook.live.com/mail/deeplink/compose',
      '_blank',
      'noopener,noreferrer'
    );
  }, []);

  const handleHide = useCallback(() => {
    dispatch(toggleWidgetPreference(WIDGET_KEY, true));
  }, [dispatch]);

  const menuActions = useMemo(
    () =>
      isConnected
        ? [
            { label: 'Hide', onClick: handleHide },
            { label: 'Disconnect account', onClick: handleReconnect },
          ]
        : [{ label: 'Hide', onClick: handleHide }],
    [isConnected, handleHide, handleReconnect]
  );

  const headerAction = (
    <Box
      display="flex"
      alignItems="center"
      gap={{ xs: 0.5, sm: 1.5 }}
      sx={{ flexShrink: 0 }}
    >
      <IconButton
        aria-label="Email options"
        size="small"
        onClick={handleCompose}
        sx={{
          width: 32,
          height: 32,
          minWidth: 32,
          minHeight: 32,
          padding: 0,
          borderRadius: '100px',
          bgcolor: 'primary.main',
          color: 'primary.main',
          border: '1px solid',
          borderColor: 'primary.main',
          '&:hover': {
            bgcolor: 'primary.main',
          },
        }}
      >
        <Icons.EditNoteOutlined
          sx={{ color: palette.primary.contrastText }}
          fontSize="small"
        />
      </IconButton>
    </Box>
  );
  const inboxHeader = (
    <Box
      display="flex"
      alignItems="center"
      sx={{
        ml: { xs: 0, sm: 1.5 },
        gap: { xs: 0.5, sm: 1 },
        whiteSpace: 'nowrap',
        flexShrink: 0,
        height: 32,
      }}
    >
      <img
        src={outlookWidgetLogoURL}
        alt="Outlook"
        style={{
          width: 'auto',
          objectFit: 'contain',
          display: 'inline-block',
          verticalAlign: 'middle',
          marginRight: 12,
          flexShrink: 0,
          height: 32,
          marginLeft: isSmallScreen ? -9 : -20,
        }}
      />

      <Typography
        component="span"
        color="primary"
        sx={{
          fontSize: { xs: '15px', sm: '16px' },
          lineHeight: '24px',
          fontWeight: 500,
          fontFamily: 'Roboto, sans-serif',
        }}
      >
        Inbox
      </Typography>

      {isConnected && outlookEmails.unreadCount > 0 ? (
        <Badge
          badgeContent={outlookEmails.unreadCount}
          sx={{
            ml: 1.5,
            '& .MuiBadge-badge': {
              backgroundColor: '#e8dcfcff',
              color: palette.getContrastText('#e8dcfcff'),
            },
          }}
        />
      ) : null}
    </Box>
  );

  return (
    <>
      <WidgetCard
        title=""
        titleAdornment={inboxHeader}
        headerAction={isConnected ? headerAction : undefined}
        menuActions={menuActions}
        ariaLabel="Outlook widget"
        minHeight="560px"
      >
        {outlookView === 'loading' ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            flex={1}
            py={4}
          >
            <CircularProgress size={32} color="primary" />
          </Box>
        ) : outlookView === 'inbox' ? (
          <OutlookInbox
            messages={outlookEmails.emails}
            isLoading={outlookEmails.isLoadingEmails}
            onViewAll={handleViewAll}
            onMessageClick={handleMessageClick}
            onRefresh={handleRefresh}
          />
        ) : outlookView === 'load-error' ? (
          <OutlookErrorState type="load-error" onAction={handleRetryLoad} />
        ) : outlookView === 'expired' ? (
          <OutlookErrorState type="expired" onAction={handleReconnect} />
        ) : outlookView === 'connect-error' ? (
          <OutlookErrorState type="connect-error" onAction={handleConnect} />
        ) : (
          /* 'connect' view */
          <Box
            display="flex"
            flexDirection="column"
            gap={2}
            justifyContent="center"
            flex={1}
            px={1}
            py={2}
          >
            <Box
              display="flex"
              gap={1.5}
              p={2}
              sx={{
                bgcolor: isDarkMode ? '#E5F6FD' : '#E1F5FE',
                borderRadius: '4px',
              }}
            >
              <Alert
                severity="info"
                variant="standard"
                aria-label="Information"
                sx={{
                  minWidth: 22,
                  p: 0,
                  bgcolor: 'transparent',
                  color: '#0288D1',
                  '& .MuiAlert-icon': { m: 0, p: 0 },
                  '& .MuiAlert-message, & .MuiAlert-action': {
                    display: 'none',
                  },
                }}
              />

              <Box color="#01579B">
                <Typography variant="subtitle2" fontWeight="bold">
                  Manage your institutional emails.
                </Typography>

                <Typography variant="body2" mt={0.5}>
                  Connect Outlook to stay on top of important emails and
                  streamline communication.
                </Typography>
              </Box>
            </Box>

            <Button
              kind="primary"
              onClick={handleConnect}
              fullWidth
              disabled={isConnecting}
              endIcon={
                isConnecting ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <LinkIcon />
                )
              }
            >
              {isConnecting ? 'Connecting…' : 'Connect Outlook account'}
            </Button>
          </Box>
        )}
      </WidgetCard>
      {!isSmallScreen && (
        <>
          <OutlookAllEmailsDialog
            open={isAllEmailsOpen}
            messages={outlookEmails.emails}
            isLoading={outlookEmails.isLoadingEmails}
            unreadCount={outlookEmails.unreadCount}
            hasMore={outlookEmails.hasMoreEmails}
            isLoadingMore={outlookEmails.isLoadingMoreEmails}
            onLoadMore={outlookEmails.fetchNextPage}
            onClose={handleAllEmailsClose}
            onMessageClick={(emailId) => handleMessageClick(emailId, true)}
            onSearch={outlookEmails.searchEmails}
          />
          <OutlookThreadDialog
            open={isThreadDialogOpen}
            thread={outlookEmails.selectedThread ?? undefined}
            isLoading={outlookEmails.isLoadingDetail}
            onClose={handleThreadClose}
          />
        </>
      )}
    </>
  );
};

export default OutlookWidget;
